import { useCallback, useMemo } from 'react';
import { queryFeatures } from '@/features/features/data';
import { buildDetailGridQueryKey } from '@/ui/composites/grid/queryKeyUtils';
import { emptyQueryResult } from '@/ui/composites/grid/queryUtils';
import { useIdsQueryFn } from '@/ui/composites/grid/useIdsQueryFn';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { FeatureDto, QueryResult } from '@flowqa/shared';

type UseDetailFeatureQueryInput = {
  entityId: string | null | undefined;
  tableKey: string;
  featureIds: Array<string | null | undefined>;
  projectId: string | null | undefined;
  organisationId: string | null | undefined;
};

const normalizeIds = (ids: Array<string | null | undefined>): string[] =>
  ids.filter((id): id is string => typeof id === 'string' && id.length > 0);

export const useDetailFeatureQuery = ({
  entityId,
  tableKey,
  featureIds,
  projectId,
  organisationId,
}: UseDetailFeatureQueryInput): {
  featureQueryKey: readonly unknown[];
  featureQueryFn: (input: GridQueryInput) => Promise<QueryResult<FeatureDto>>;
  featureIdsKey: string;
  normalizedFeatureIds: string[];
} => {
  const normalizedFeatureIds = useMemo(() => normalizeIds(featureIds), [featureIds]);
  const featureIdsKey = useMemo(
    () => JSON.stringify([...normalizedFeatureIds].sort()),
    [normalizedFeatureIds],
  );
  const featureQueryKey = buildDetailGridQueryKey(
    'features/query',
    tableKey,
    entityId,
    featureIdsKey,
    organisationId,
  );

  const fetchFeatureIds = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<FeatureDto>> => {
      if (!projectId) {
        return Promise.resolve(emptyQueryResult(input));
      }
      return (await queryFeatures(
        projectId,
        organisationId ?? undefined,
        input,
      )) as QueryResult<FeatureDto>;
    },
    [organisationId, projectId],
  );

  const featureQueryFn = useIdsQueryFn<FeatureDto>(normalizedFeatureIds, fetchFeatureIds);

  return { featureQueryKey, featureQueryFn, featureIdsKey, normalizedFeatureIds };
};
